import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '../config/api';

const EventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  useEffect(() => {
    fetchEvents();
    
    // Listen for event updates from EventMaster
    const handleEventUpdate = () => {
      fetchEvents();
    };
    
    window.addEventListener('eventUpdated', handleEventUpdate);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('eventUpdated', handleEventUpdate);
    };
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, selectedFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      // console.log('Fetching events from:', `${API_CONFIG.BASE_URL}/api/events/all-public`);
      const response = await axios.get('/api/events/all-public');
      // console.log('Events response:', response.data);
      setEvents(response.data || []);
    } catch (error) {
      // // console.log('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    // Safety check: ensure events is always an array
    const safeEvents = Array.isArray(events) ? events : [];
    let filtered = [];

    switch (selectedFilter) {
      case 'upcoming':
        filtered = safeEvents.filter(event => {
          // Only show events with eventType = 'upcoming'
          return event.eventType === 'upcoming';
        });
        break;
      case 'ongoing':
        filtered = safeEvents.filter(event => {
          // Only show events with eventType = 'ongoing'
          return event.eventType === 'ongoing';
        });
        break;
      case 'past':
        filtered = safeEvents.filter(event => {
          // Only show events with eventType = 'past'
          return event.eventType === 'past';
        });
        break;
      case 'abandoned':
        filtered = safeEvents.filter(event => event.eventType === 'abandoned');
        break;
      default:
        filtered = safeEvents;
    }

    // Debug logging
    // console.log('Filter:', selectedFilter);
    // console.log('All events:', safeEvents.map(e => ({ name: e.eventName, type: e.eventType })));
    // console.log('Filtered events:', filtered.map(e => ({ name: e.eventName, type: e.eventType })));

    setFilteredEvents(filtered);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getEventStatus = (event) => {
    // Use the eventType from database directly
    return event.eventType || 'upcoming';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-yellow-100 text-yellow-800';
      case 'ongoing': return 'bg-blue-100 text-blue-800';
      case 'past': return 'bg-gray-100 text-gray-800';
      case 'abandoned': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'upcoming': return '📅';
      case 'ongoing': return '🔄';
      case 'past': return '✅';
      case 'abandoned': return '❌';
      default: return '📋';
    }
  };

  const handleViewRules = (event) => {
    setSelectedEvent(event);
    setShowRulesModal(true);
  };

  const handleCloseRulesModal = () => {
    setShowRulesModal(false);
    setSelectedEvent(null);
  };

  const handleRegisterForEvent = () => {
    // Close the modal first
    setShowRulesModal(false);
    setSelectedEvent(null);
    
    // Navigate to registration page
    navigate('/register');
  };

  const handleViewDescription = (event) => {
    setSelectedEvent(event);
    setShowDescriptionModal(true);
  };

  const handleCloseDescriptionModal = () => {
    setShowDescriptionModal(false);
    setSelectedEvent(null);
  };

  // console.log('EventsPage rendering - loading:', loading, 'events:', events.length, 'filtered:', filteredEvents.length);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-4 text-gray-600">Loading events...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Cricket <span className="text-orange-600">Events</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover all cricket events - from upcoming tournaments to past championships
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {[
            { key: 'all', label: 'All Events', icon: '📋' },
            { key: 'upcoming', label: 'Upcoming', icon: '📅' },
            { key: 'ongoing', label: 'Ongoing', icon: '🔄' },
            { key: 'past', label: 'Past', icon: '✅' },
            { key: 'abandoned', label: 'Abandoned', icon: '❌' }
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setSelectedFilter(filter.key)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                selectedFilter === filter.key
                  ? 'bg-orange-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'
              }`}
            >
              <span>{filter.icon}</span>
              {filter.label}
            </button>
          ))}
        </div>

        {/* Events Count */}
        <div className="text-center mb-8">
          <p className="text-gray-600">
            Showing {filteredEvents.length} {selectedFilter === 'all' ? 'events' : selectedFilter} events
          </p>
        </div>

        {/* Events Grid */}
        {filteredEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event) => {
              const status = getEventStatus(event);
              return (
                <div
                  key={event._id}
                  className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex flex-col h-full"
                >
                  {/* Event Image - Fixed height */}
                  {event.image || event.photoUrl ? (
                    <img
                      src={event.image || event.photoUrl}
                      alt={event.eventName || event.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.src = '/images/heroSection_img.jpg';
                      }}
                    />
                  ) : (
                    <img
                      src="/images/heroSection_img.jpg"
                      alt="Cricket Event"
                      className="w-full h-48 object-cover"
                    />
                  )}

                  {/* Event Content - Flex grow to fill space */}
                  <div className="p-6 flex flex-col flex-grow">
                    {/* Status Badge */}
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                        {getStatusIcon(status)} {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>

                    {/* Event Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {event.eventName || event.title}
                    </h3>

                    {/* Event Date */}
                    <p className="text-blue-600 font-medium mb-3">
                      {formatDate(event.startDate)}
                    </p>

                    {/* Event Location - Fixed height */}
                    <p className="text-gray-700 mb-4 min-h-[3rem] flex items-center">
                      <span className="font-medium">📍 Location:</span> {event.location || '118- Ward No.2, Kabir Nagar, Raipur'}
                    </p>

                    {/* View Description Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDescription(event);
                      }}
                      className="w-full bg-transparent border border-blue-500 text-blue-600 py-2 px-4 rounded-lg 
                               font-semibold text-sm transition-all duration-200 hover:bg-blue-500 hover:text-white
                               hover:shadow-lg transform hover:scale-105 flex items-center justify-center mb-3"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Description
                    </button>

                    {/* Rules & Regulations Button */}
                    <button
                      onClick={() => handleViewRules(event)}
                      className="w-full bg-transparent border border-orange-500 text-orange-600 py-2 px-4 rounded-lg 
                               font-semibold text-sm transition-all duration-200 hover:bg-orange-500 hover:text-white
                               hover:shadow-lg transform hover:scale-105 flex items-center justify-center mb-3"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      View Rules & Regulations
                    </button>

                    {/* Event Type - Pushed to bottom */}
                    <div className="text-sm text-gray-500 mt-auto">
                      Type: {event.eventType || 'Tournament'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl p-12 max-w-2xl mx-auto shadow-lg">
              <svg className="w-16 h-16 mx-auto mb-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                No {selectedFilter === 'all' ? '' : selectedFilter} Events Found
              </h3>
              <p className="text-gray-600 mb-6">
                {selectedFilter === 'all' 
                  ? 'No events have been scheduled yet. Check back later for exciting cricket events!'
                  : `No ${selectedFilter} events available at the moment.`
                }
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Admin:</strong> Add events through the admin dashboard to see them here.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rules Modal */}
        {showRulesModal && selectedEvent && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="relative max-w-2xl w-full max-h-[90vh] overflow-hidden bg-white rounded-2xl shadow-2xl">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">{selectedEvent.eventName || selectedEvent.title}</h3>
                    <p className="text-orange-100 mt-1">
                      {formatDate(selectedEvent.startDate)}
                    </p>
                  </div>
                  <button
                    onClick={handleCloseRulesModal}
                    className="text-white hover:text-orange-200 p-2 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Event Rules & Guidelines
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {selectedEvent.rules || selectedEvent.description ? (
                        <div className="prose prose-sm max-w-none">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                            {selectedEvent.rules || selectedEvent.description}
                          </p>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-gray-500">No specific rules have been provided for this event.</p>
                          <p className="text-sm text-gray-400 mt-2">Please contact the organizers for more details.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Event Location
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {selectedEvent.location || "118, Ward No 2, Kabir Nagar, Raipur"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={handleCloseRulesModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleRegisterForEvent}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium transition-colors"
                >
                  Register for Event
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Description Modal */}
        {showDescriptionModal && selectedEvent && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="relative max-w-2xl w-full max-h-[90vh] overflow-hidden bg-white rounded-2xl shadow-2xl">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">{selectedEvent.eventName || selectedEvent.title}</h3>
                    <p className="text-blue-100 mt-1">
                      {formatDate(selectedEvent.startDate)}
                    </p>
                  </div>
                  <button
                    onClick={handleCloseDescriptionModal}
                    className="text-white hover:text-blue-200 p-2 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Event Description
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {selectedEvent.description || 'No description available for this event.'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Event Location
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {selectedEvent.location || "118- Ward No.2, Kabir Nagar, Raipur"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={handleCloseDescriptionModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleRegisterForEvent}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium transition-colors"
                >
                  Register for Event
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage; 
